import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { useDisclosure } from '@mantine/hooks';
import ColumnPairingModal, {
  ColumnPairingModalProps,
} from 'components/forms/remote-source/config/chastia-config-form/device-types/columns/ColumnPairingModal';

type ColumnPairingModalParams = Omit<ColumnPairingModalProps, 'opened' | 'onClose'>;
type OpenColumnPairingModal = (params: ColumnPairingModalParams) => void;

interface IColumnPairingModalContext {
  openColumnPairingModal: OpenColumnPairingModal;
}

const ColumnPairingModalContext = createContext<IColumnPairingModalContext>(undefined!);

/**
 * Provides the ColumnPairingModalContext.
 */
export function ColumnPairingModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [key, setKey] = useState(nanoid);
  const [params, setParams] = useState<ColumnPairingModalParams>(undefined!);

  const colNames = useMemo(() => Array.from({ length: 50 }, (_, i) => `D${i + 1}`), []);

  const openColumnPairingModal: OpenColumnPairingModal = useCallback((params) => {
    setParams(params);
    open();
    setKey(nanoid());
  }, []);

  const value = useMemo(() => ({ openColumnPairingModal }), [openColumnPairingModal]);

  return (
    <ColumnPairingModalContext.Provider value={value}>
      {children}
      <datalist id="column-names">
        {colNames.map((colName) => (
          <option key={colName} value={colName} />
        ))}
      </datalist>
      <ColumnPairingModal key={key} opened={opened} onClose={close} {...params} />
    </ColumnPairingModalContext.Provider>
  );
}

/**
 * Uses the ColumnPairingModalContext.
 */
export function useColumnPairingModal() {
  return useContext(ColumnPairingModalContext);
}
